import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart"
import { BarChart, Bar, XAxis, YAxis, Cell } from "recharts"
import { COLORS } from "./constants"
import type { ChartConfigEntry, RaceCount, RacePerformanceStat } from "./types"

interface PerformanceChartsProps {
  racePerformanceStats: RacePerformanceStat[]
  raceStats: RaceCount[]
  raceChartConfig: Record<string, ChartConfigEntry>
}

export const PerformanceCharts = ({
  racePerformanceStats,
  raceStats,
  raceChartConfig,
}: PerformanceChartsProps) => {
  const byPGW = [...racePerformanceStats].sort((a, b) => b.avgPGW - a.avgPGW)

  const colorForRace = (name: string) =>
    COLORS[raceStats.findIndex((r) => r.name === name) % COLORS.length]

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Average Points Chart */}
      <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
        <CardHeader>
          <CardTitle className="text-base sm:text-lg text-gray-900 dark:text-gray-100">
            Puntos Promedio por Raza
          </CardTitle>
        </CardHeader>
        <CardContent className="p-2">
          <ChartContainer config={raceChartConfig} className="w-full">
            <BarChart
              data={racePerformanceStats}
              margin={{ top: 0, right: 0, left: -20, bottom: 0 }}
              barSize={60}
            >
              <XAxis
                dataKey="name"
                angle={-45}
                textAnchor="end"
                height={60}
                interval={0}
                fontSize={9}
                tick={{ fontSize: 9, fill: "var(--tw-text-opacity)" }}
              />
              <YAxis fontSize={9} />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    formatter={(value) => [`${Number(value).toFixed(1)} pts promedio`]}
                    labelFormatter={(label) => `Raza: ${label}`}
                    className="dark:text-gray-100"
                  />
                }
              />
              <Bar dataKey="avgPoints" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]}>
                {racePerformanceStats.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={colorForRace(entry.name)} />
                ))}
              </Bar>
            </BarChart>
          </ChartContainer>
        </CardContent>
      </Card>

      {/* Win Rates Chart */}
      <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
        <CardHeader>
          <CardTitle className="text-base sm:text-lg text-gray-900 dark:text-gray-100">
            Porcentajes de Victoria (PGW)
          </CardTitle>
        </CardHeader>
        <CardContent className="p-2">
          <ChartContainer config={raceChartConfig} className="w-full">
            <BarChart data={byPGW} margin={{ top: 0, right: 0, left: -20, bottom: 0 }} barSize={60}>
              <XAxis
                dataKey="name"
                angle={-45}
                textAnchor="end"
                height={60}
                interval={0}
                fontSize={9}
                tick={{ fontSize: 9, fill: "var(--tw-text-opacity)" }}
              />
              <YAxis fontSize={9} />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    formatter={(value) => [`${Number(value).toFixed(1)}%`, "PGW"]}
                    labelFormatter={(label) => `Raza: ${label}`}
                    className="dark:text-gray-100"
                  />
                }
              />
              <Bar dataKey="avgPGW" fill="hsl(var(--success))" radius={[4, 4, 0, 0]}>
                {byPGW.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={colorForRace(entry.name)} />
                ))}
              </Bar>
            </BarChart>
          </ChartContainer>
        </CardContent>
      </Card>
    </div>
  )
}
